import { useEffect } from 'react';
import { useCookies } from 'react-cookie';

type ScriptStatus = 'idle' | 'loading' | 'ready' | 'error';

export type ExternalElementTag = 'script' | 'img' | 'meta';
export type ExternalElementTarget = 'head' | 'body';
type ExternalElementAttributes = Record<string, string | undefined>;
type UseExternalScriptOptions = {
  requiredCookies?: string[];
  tagName?: ExternalElementTag;
  target?: ExternalElementTarget;
};

const handleError = () => {
  return 'error';
};

const getExternalElementKey = (
  tagName: ExternalElementTag,
  target: ExternalElementTarget,
  src: string | undefined,
  attributes: ExternalElementAttributes,
  id: string | undefined,
) => {
  if (id) {
    return id;
  }

  const serializedAttributes = Object.keys(attributes)
    .sort()
    .map((key) => `${key}:${attributes[key] ?? ''}`)
    .join('|');

  return encodeURIComponent(`${tagName}|${target}|${src ?? ''}|${serializedAttributes}`);
};

export const useExternalScript = (
  src: string | undefined,
  attributes: ExternalElementAttributes = {},
  options: UseExternalScriptOptions = {},
): ScriptStatus => {
  const { id, ...elementAttributes } = attributes;
  const requiredCookies = options.requiredCookies ?? [];
  const [cookies] = useCookies(requiredCookies);
  const tagName = options.tagName ?? 'script';
  const target = options.target ?? 'head';
  const externalElementKey = getExternalElementKey(tagName, target, src, elementAttributes, id);
  const hasRequiredCookies = requiredCookies.every((cookieName) => cookies[cookieName] !== undefined);

  useEffect(() => {
    return () => {
      const existingScript = id
        ? document.getElementById(id)
        : document.querySelector<HTMLElement>(`[data-external-element-key="${externalElementKey}"]`);

      existingScript?.removeEventListener('error', handleError);
    };
  }, [externalElementKey, id]);

  if (!hasRequiredCookies || (tagName !== 'meta' && !src)) {
    return 'idle';
  }

  if (typeof document === 'undefined') {
    return 'idle';
  }

  const externalElement = id
    ? document.getElementById(id)
    : document.querySelector<HTMLElement>(`[data-external-element-key="${externalElementKey}"]`);

  if (externalElement) {
    return 'ready';
  }

  const rootElement = target === 'body' ? document.body : document.head;

  if (!rootElement) {
    return 'idle';
  }

  const element = document.createElement(tagName);

  if (id) {
    element.id = id;
  }

  element.setAttribute('data-external-element-key', externalElementKey);

  if (tagName === 'script') {
    const script = element as HTMLScriptElement;
    script.async = true;

    if (src) {
      script.src = src;
    }

    script.addEventListener('error', handleError);
  } else if (src) {
    element.setAttribute('src', src);
  }

  Object.keys(elementAttributes).forEach((key) => {
    const value = elementAttributes[key];

    if (value !== undefined) {
      element.setAttribute(key, value);
    }
  });

  rootElement.appendChild(element);

  return 'ready';
};
